/**
 * returnTo: URL 쿼리 → sessionStorage 동기화 후, 카카오 콜백·연락처 인증 완료 등에서 session 기준으로 이동.
 */

/**
 * 돌아갈 주소(`returnTo`)를 받는 **한 문** — 같은 사이트 안의 절대 경로만 통과시킨다.
 * ⛔ `//evil.com` · `/\evil.com` · `https:` · `javascript:` 같은 바깥 주소와, 그것을 인코딩해 감춘 모양
 *   (`%2F%2F` · 두 번 인코딩 · 탭·줄바꿈 끼우기)은 전부 `null` 이다 — 로그인 뒤 남의 사이트로 보내는 구멍이 된다.
 * ⚠️ `searchParams.get` 이 이미 한 번 디코드한 값이다 — `/` 로 시작하는 경로는 **더 디코드하지 않는다**.
 *   경로 안의 `%26`·`%2B`·`%23`·`%25` 는 질의 값의 일부라 벗기면 `&`·`+`·`#`·`%` 가 질의를 쪼갠다(질문이 잘린다).
 *   통째로 인코딩된 값(`%2Fservice…`)만 한 번 벗긴다. 같은 값을 몇 번 거쳐도 결과가 같다.
 * @param {string | null | undefined} returnToParam — `searchParams.get('returnTo')` · 세션에 둔 값 등
 * @returns {string | null} 같은 사이트의 `/…` 경로(질의 값의 인코딩은 그대로). 빈 값·바깥 주소면 null
 */
export function normalizeReturnToParam(returnToParam: string | null | undefined): string | null {
  if (returnToParam == null || returnToParam === '') return null;
  let s = String(returnToParam).trim();
  if (!s.startsWith('/')) {
    try {
      s = decodeURIComponent(s).trim();
    } catch {
      return null;
    }
  }
  if (!s) return null;
  return isSameOriginPath(s) ? s : null;
}

/**
 * 같은 사이트 안의 절대 경로인가 — `/` 하나로 시작하고, 몇 번을 디코드해도 바깥 주소로 읽히지 않는다.
 * 🔑 브라우저는 `\` 를 `/` 로, 탭·줄바꿈을 지운 채 읽는다 — 그래서 그 둘이 끼면 통째로 거른다.
 */
export function isSameOriginPath(value: string): boolean {
  let current = value;
  for (let round = 0; round < MAX_DECODE_ROUNDS; round += 1) {
    if (!isPlainAbsolutePath(current)) return false;
    let next = current;
    try {
      next = decodeURIComponent(current);
    } catch {
      return true;
    }
    if (next === current) return true;
    current = next;
  }
  return false;
}

/**
 * URL에 붙은 returnTo를 sessionStorage에 반영. 없으면 키 제거(로그인·게스트 페이지와 동일).
 */
export function syncReturnToUrlToSession(returnToParam: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  const normalized = normalizeReturnToParam(returnToParam);
  if (normalized) {
    sessionStorage.setItem('returnTo', normalized);
  } else {
    sessionStorage.removeItem('returnTo');
  }
}

/**
 * 로그인 직후 이동 등 — 이미 정규화된 path 문자열을 세션에 저장 (카카오 OAuth 직전 등).
 */
export function persistReturnToRawPath(rawPath: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  if (rawPath == null || rawPath === '') {
    sessionStorage.removeItem('returnTo');
    return;
  }
  const n = normalizeReturnToParam(String(rawPath));
  if (n) sessionStorage.setItem('returnTo', n);
  else sessionStorage.removeItem('returnTo');
}

export function getReturnToFromSession(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem('returnTo');
}

/** 세션의 돌아갈 곳을 지운다 — 연락처 인증이 그 주소로 보낸 뒤(다 쓴 값이 다음 가입 흐름에 남지 않게) */
export function clearReturnToSession(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem('returnTo');
}

/**
 * 연락처 인증으로 떠밀 때 달 «보려던 주소» — 로그인을 시작한 자리가 세션에 남긴 돌아갈 곳(같은 사이트 경로만)이 있으면 그것, 없으면 지금 주소.
 * 🔑 창 안 로그인(가입 안내 창)은 지금 주소가 랜딩 `/`·`/ask` 다 — 그대로 달면 인증 뒤 누른 자리를 잃는다. 로그인 직전 세션에 남긴 값이 이긴다.
 * @param currentPath 지금 주소(pathname + search)
 */
export function pendingReturnToOr(currentPath: string): string {
  return normalizeReturnToParam(getReturnToFromSession()) ?? currentPath;
}

/**
 * 사용자를 다른 화면(로그인·연락처 인증)으로 떠밀 때 **보려던 주소를 `returnTo` 로 단다.**
 * 가드와 provider 가 같은 식을 쓴다 — 한쪽만 달면 뒤에 오는 `replace` 가 값을 지운다.
 *
 * @param target   보낼 화면(`/auth/login` · `/auth/contact-verification`)
 * @param fullPath 지금 보려던 주소 — pathname + search
 * @returns `${target}?returnTo=<encoded fullPath>`
 */
export function withReturnTo(target: string, fullPath: string): string {
  const query = new URLSearchParams({ returnTo: fullPath }).toString();
  return `${target}?${query}`;
}

// ---- 이하 비공개 ----------------------------------------------------------

/** 겹겹이 인코딩한 값을 벗기는 최대 횟수 — 넘으면 바깥 주소로 본다 */
const MAX_DECODE_ROUNDS = 5;

/** `/` 하나로 시작하고 `//`·`/\` 가 아니며, `\`·제어 문자가 없다 */
function isPlainAbsolutePath(value: string): boolean {
  if (!value.startsWith('/') || value.startsWith('//')) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(value)) return false;
  return true;
}
